/**
 * Theme and language controls.
 *
 * Both are per-device preferences rather than account settings: the same
 * merchant may want dark on the counter iPad, and the same customer may switch
 * to Chinese only for the duration of one order.
 *
 * Nothing here sets a colour. The appearance choice is handed to
 * `AppearanceProvider`, which is the one place that toggles the `dark` class on
 * the document root, so every `dark:` utility in every component follows it.
 */

import { type ReactNode } from 'react';
import { Pressable, View } from 'react-native';

import { Moon, SlidersHorizontal, Sun } from 'lucide-react-native';

import { LOCALES } from '@yummy/shared';

import { Button } from './ui/button';
import { Icon } from './ui/icon';
import { Text } from './ui/text';
import { Row, Stack } from './layout';
import { Modal } from './overlay';
import { useAppearance, type ThemePreference } from '../providers/appearance';
import { useLocale, type LocalePreference } from '../providers/locale';
import { useResponsive } from '../lib/hooks';
import { cn } from '../lib/utils';

/**
 * The heading above a row of choices.
 *
 * Small and letter-spaced rather than a section title: inside the sheet these
 * are two groups in one panel, not two screens, and a full-size heading on each
 * would make the sheet read as a page.
 *
 * @param props The group's name.
 * @returns The label.
 */
function ChoiceLabel({ children }: { children: ReactNode }) {
  return (
    <Text className="text-muted-foreground text-xs font-bold uppercase tracking-wider">
      {children}
    </Text>
  );
}

/**
 * A row of mutually exclusive chips.
 *
 * The shape the menu already uses for a dish's options, applied to settings. A
 * chip costs one line where a radio row costs three, which is the difference
 * between the whole panel fitting in a sheet and it needing to scroll.
 *
 * @param props Options, current value, and handler.
 * @returns The wrapped row of chips.
 */
function ChoiceChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <View className="flex-row flex-wrap gap-2">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            onPress={() => onChange(option.value)}
            accessibilityRole="radio"
            aria-checked={selected}
            // The selected chip keeps its neighbours' height: the heavier border
            // is paid for out of the vertical padding rather than added to it.
            className={cn(
              'border-border rounded-full border',
              selected
                ? 'border-primary bg-primary-soft border-2 px-4 py-1.5'
                : 'bg-card px-4 py-2'
            )}>
            <Text
              className={cn(
                'text-sm',
                selected ? 'text-primary-strong font-bold' : 'text-muted-foreground font-medium'
              )}>
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * Light / dark / system picker.
 *
 * The selection is a stored preference, not an applied colour: the provider
 * resolves `system` against the device and owns the `dark` class, so a merchant
 * whose iPad switches to dark at sunset sees the app follow.
 *
 * @param props Optional title override.
 * @returns The appearance choices.
 */
export function AppearanceControl({ title }: { title?: string }) {
  const { preference, setPreference } = useAppearance();
  const { t } = useLocale();

  return (
    <Stack gap="sm">
      <ChoiceLabel>{title ?? t('common.appearance')}</ChoiceLabel>
      <ChoiceChips<ThemePreference>
        value={preference}
        onChange={setPreference}
        options={[
          { value: 'system', label: t('common.themeSystem') },
          { value: 'light', label: t('common.themeLight') },
          { value: 'dark', label: t('common.themeDark') },
        ]}
      />
    </Stack>
  );
}

/**
 * Language picker.
 *
 * Each option is labelled in its own language, which is the only way a speaker
 * of that language can reliably find it.
 *
 * @param props Optional title override.
 * @returns The language choices.
 */
export function LanguageControl({ title }: { title?: string }) {
  const { preference, setPreference, t } = useLocale();

  return (
    <Stack gap="sm">
      <ChoiceLabel>{title ?? t('common.language')}</ChoiceLabel>
      <ChoiceChips<LocalePreference>
        value={preference}
        onChange={setPreference}
        options={[
          // `localeSystem` spells out "follow device language" in full, which is
          // the right sentence for a labelled settings row and too long for a
          // chip beside two others. The short form says the same thing in the
          // space a chip has.
          { value: 'system', label: t('common.localeSystemShort') },
          ...LOCALES.map((locale) => ({
            value: locale.code,
            label: locale.nativeLabel,
          })),
        ]}
      />
    </Stack>
  );
}

/**
 * Both controls, stacked.
 *
 * The body of the preferences sheet, and the reason the sheet is only a host:
 * the two settings are defined once, here, and every route to them renders this.
 *
 * @returns The language and appearance groups in one panel.
 */
export function PreferencesPanel() {
  return (
    <Stack gap="xl">
      <LanguageControl />
      <AppearanceControl />
    </Stack>
  );
}

/**
 * The language and appearance sheet.
 *
 * A sheet rather than a screen, because the customer app has no settings screen.
 * My orders had been doubling as one, which left the panel stranded below every
 * order and unreachable from the menu a QR actually lands on. Drawn from the top
 * bar, the same panel is one tap away on every screen.
 *
 * The panel takes the shape its window expects. Up to a phone it is a bottom
 * sheet, which is where a thumb already is. From the tablet breakpoint up it is
 * a centred dialog: a sheet pinned to the bottom of a wide window is both
 * stretched and easy to miss, and on a short landscape window it would cover
 * most of the height it is anchored to.
 *
 * @param props Visibility and the dismiss handler.
 * @returns The sheet or dialog, rendered in the portal host.
 */
export function PreferencesSheet({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const { t } = useLocale();
  const { isTablet } = useResponsive();

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      placement={isTablet ? 'center' : 'bottom'}
      // Only reaches the centred dialog, but a preferences panel is narrower
      // than a form: at the sheet's default width the two chip rows would sit in
      // a mostly empty band.
      maxWidth={480}
      title={t('common.languageAndAppearance')}>
      <PreferencesPanel />
    </Modal>
  );
}

/**
 * The one control for both per-device preferences.
 *
 * A round icon button. It used to spell the setting out on its face — `EN ·
 * Light` — but that pill wanted the room the bar's own title needed, and the
 * state is one tap away in the sheet. The label carries the meaning instead, so
 * the control is not an unlabelled glyph to assistive technology.
 *
 * Renders the button alone. Opening `PreferencesSheet` is the caller's job, and
 * the two are kept apart on purpose: the sheet renders an element where it is
 * declared, so a button that carried its own sheet would put that element in the
 * middle of whatever row it sits in — as a flex child it adds the row's gap
 * after the button and pushes it off the edge it was aligned to.
 *
 * @param props The press handler and extra classes.
 * @returns The icon button.
 */
export function PreferencesButton({
  onPress,
  className,
}: {
  onPress: () => void;
  className?: string;
}) {
  const { t } = useLocale();

  return (
    <Button
      variant="outline"
      size="icon"
      className={cn('rounded-full', className)}
      onPress={onPress}
      accessibilityLabel={t('common.languageAndAppearance')}>
      {/* `size-[22px]` rather than the `size` prop alone: Button's base styles any
          SVG without a `size-` class to 16px, which would leave this icon smaller
          than the back control's the bar sets it beside. */}
      <Icon
        as={SlidersHorizontal}
        size={22}
        aria-hidden
        className="text-muted-foreground size-[22px]"
      />
    </Button>
  );
}

/**
 * A compact two-button toggle for app headers.
 *
 * The theme button follows the *resolved* mode rather than the stored
 * preference, so pressing it always moves the screen to the other one, even
 * when the preference is `system`.
 *
 * @param props Layout direction; `vertical` is for the collapsed merchant rail,
 *   which is 76 points wide and cannot hold two pills side by side.
 * @returns The two pill buttons.
 */
export function QuickPreferences({
  orientation = 'horizontal',
}: {
  orientation?: 'horizontal' | 'vertical';
} = {}) {
  const { mode, setPreference } = useAppearance();
  const { locale, setPreference: setLocalePreference, t } = useLocale();

  const nextTheme: ThemePreference = mode === 'dark' ? 'light' : 'dark';
  const nextLocale: LocalePreference = locale === 'en' ? 'zh-Hant' : 'en';

  // The language button carries the name of the language it switches to, in
  // that language: '中文' has to read as Chinese to someone who cannot read the
  // language currently in force.
  const button = (content: ReactNode, onPress: () => void, accessibilityLabel: string) => (
    <Button
      variant="outline"
      size="sm"
      className="rounded-full"
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}>
      {content}
    </Button>
  );

  const buttons = (
    <>
      {button(
        <Text className="text-muted-foreground text-sm font-semibold">
          {locale === 'en' ? '中文' : 'EN'}
        </Text>,
        () => setLocalePreference(nextLocale),
        t('common.switchLanguage')
      )}
      {button(
        <Icon
          as={mode === 'dark' ? Sun : Moon}
          size={16}
          aria-hidden
          className="text-muted-foreground"
        />,
        () => setPreference(nextTheme),
        mode === 'dark' ? t('common.switchToLight') : t('common.switchToDark')
      )}
    </>
  );

  return orientation === 'vertical' ? (
    <Stack gap="sm" align="center">
      {buttons}
    </Stack>
  ) : (
    <Row gap="sm" align="center">
      {buttons}
    </Row>
  );
}
